import { colorDeEstatusOrden } from '@/shared/lib/coloresDeEstatus'
import type {
  PurchaseOrderRH,
  PurchaseOrderRHResponse,
  PurchaseOrderRHFilters,
  PurchaseOrderRHStats,
  PurchaseOrderRHComment
} from '@/types/purchaseOrderRH.types'
import { httpRequest, construirQuery } from '@/shared/lib/http'
import type { ColorDeBadge } from '@/shared/components/Badge'

const base = '/admin/purchase-orders'

export const purchaseOrdersRHService = {
  async getOrders(filters?: PurchaseOrderRHFilters): Promise<PurchaseOrderRHResponse> {
    const query = construirQuery({ ...filters } as Record<string, string | number | boolean | undefined>)
    return (await httpRequest<PurchaseOrderRHResponse>(`${base}${query}`))!
  },

  async getOrderById(orderId: string): Promise<PurchaseOrderRH> {
    const response = await httpRequest<PurchaseOrderRH>(`${base}/${orderId}`)
    return response!
  },

  calculateStats(orders: PurchaseOrderRH[]): PurchaseOrderRHStats {
    const total = orders.length
    const confirmed = orders.filter(o => o.status === 'confirmed').length
    const rejected = orders.filter(o => o.status === 'rejected').length
    const pending = orders.filter(o => o.status === 'pending' || o.status === 'draft').length
    const inProgress = orders.filter(o => o.status === 'in_progress').length
    const completed = orders.filter(o => o.status === 'completed').length
    const cancelled = orders.filter(o => o.status === 'cancelled').length

    return {
      total,
      confirmed,
      rejected,
      pending,
      inProgress,
      completed,
      cancelled,
      confirmedPercentage: total > 0 ? (confirmed / total) * 100 : 0,
      rejectedPercentage: total > 0 ? (rejected / total) * 100 : 0
    }
  },

  formatCurrency(amount: string, currency: string): string {
    const num = parseFloat(amount)
    const code = (currency || 'MXN').toUpperCase()
    return `${code} $${num.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  },

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A'
    // Las columnas DATE llegan como 2026-10-01, y asi JS las lee como medianoche
    // UTC: al pintarlas en horario de Mexico se veian un dia antes. Cuando no
    // trae hora se arma la fecha local a mano; con timestamp completo no aplica.
    const soloFecha = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString)
    const fecha = soloFecha
      ? new Date(Number(soloFecha[1]), Number(soloFecha[2]) - 1, Number(soloFecha[3]))
      : new Date(dateString)
    return fecha.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
  },

  getSupplierName(supplier: any): string {
    if (!supplier) return 'Sin nombre'
    return supplier.legalName || supplier.mainContactName || 'Sin nombre'
  },

  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      draft: 'Borrador',
      pending: 'Pendiente',
      confirmed: 'Confirmada',
      rejected: 'Rechazada',
      in_progress: 'En Progreso',
      completed: 'Completada',
      cancelled: 'Cancelada'
    }
    return labels[status] || status
  },

  getStatusColor(status: string): ColorDeBadge {
    return colorDeEstatusOrden(status)
  },

  async addComment(orderId: string, comment: string): Promise<void> {
    await httpRequest(`${base}/${orderId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ comment }),
    })
  },

  async getComments(orderId: string): Promise<PurchaseOrderRHComment[]> {
    const response = await httpRequest<PurchaseOrderRHComment[]>(`${base}/${orderId}/comments`)
    return response!
  },

  async getInvoices(orderId: string): Promise<import('@/types/purchaseOrderRH.types').Invoice[]> {
    const response = await httpRequest<import('@/types/purchaseOrderRH.types').Invoice[]>(`${base}/${orderId}/invoices`)
    return response!
  },

  async getPayments(orderId: string, invoiceId: string): Promise<import('@/types/purchaseOrderRH.types').Payment[]> {
    const response = await httpRequest<import('@/types/purchaseOrderRH.types').Payment[]>(`${base}/${orderId}/invoices/${invoiceId}/payments`)
    return response!
  },

  async getAllPayments(orderId: string): Promise<import('@/types/purchaseOrderRH.types').Payment[]> {
    const order = await this.getOrderById(orderId)
    const invoices = await this.getInvoices(orderId)

    if (invoices.length === 0) return []

    const paymentsPromises = invoices.map(invoice =>
      this.getPayments(orderId, invoice.id).then(payments =>
        payments.map(payment => ({
          ...payment,
          payment_method: (payment as any).payment_method || 'N/A',
          reference_number: (payment as any).reference_number || 'N/A',
          supplier_name: this.getSupplierName(order.supplier),
          supplier_rfc: order.supplier?.rfc || 'N/A',
          invoice_id: invoice.id,
          invoice_number: invoice.supplier_invoice_number || payment.invoice_number,
          invoice_status: invoice.status,
          invoice_total: invoice.invoice_total,
          xml_url: invoice.xml_url,
          pdf_url: invoice.pdf_url,
          complement_status: (payment as any).complement_status,
          complement_validation_notes: (payment as any).complement_validation_notes,
          complement_xml_url: (payment as any).complement_xml_url,
          complement_pdf_url: (payment as any).complement_pdf_url
        }))
      )
    )
    const paymentsArrays = await Promise.all(paymentsPromises)
    return paymentsArrays.flat()
  },

  async createPayment(
    orderId: string,
    invoiceId: string,
    data: {
      amount: number
      payment_date: string
      payment_method?: string
      reference_number?: string
      notes?: string
    }
  ): Promise<import('@/types/purchaseOrderRH.types').Payment> {
    const response = await httpRequest<import('@/types/purchaseOrderRH.types').Payment>(
      `${base}/${orderId}/invoices/${invoiceId}/payments`,
      { method: 'POST', body: JSON.stringify(data) },
    )
    return response!
  },

  /**
   * Pago que cubre una o varias facturas del mismo proveedor (SP-208).
   *
   * Va como FormData porque puede traer el comprobante. Las facturas viajan en
   * un solo campo como JSON: en multipart todo es texto, y mandar `invoiceIds[]`
   * repetido deja una llave que el ValidationPipe del back no reconoce y rebota
   * la peticion entera.
   */
  async createBatchPayment(data: {
    invoiceIds: string[]
    amount: number
    payment_date: string
    payment_method?: string
    reference_number?: string
    notes?: string
    receipt?: File | null
  }): Promise<{ payment_group_id: string | null }> {
    const formData = new FormData()
    formData.append('invoiceIds', JSON.stringify(data.invoiceIds))
    formData.append('amount', String(data.amount))
    formData.append('payment_date', data.payment_date)
    if (data.payment_method) formData.append('payment_method', data.payment_method)
    if (data.reference_number) formData.append('reference_number', data.reference_number)
    if (data.notes) formData.append('notes', data.notes)
    if (data.receipt) formData.append('receipt', data.receipt)

    // Sin Content-Type a mano: con FormData lo arma el navegador con su
    // boundary, y ponerlo rompe la subida.
    const response = await httpRequest<{ payment_group_id: string | null }>(
      `${base}/payments`,
      { method: 'POST', body: formData },
    )
    return response!
  },

  /**
   * OJO: la ruta de admin no existe en el backend. Subir facturas y consultar
   * las lineas disponibles solo se puede desde el portal del proveedor, y de
   * eso se encarga features/proveedores/api/purchaseOrders.service.ts, que es
   * el que usan las pantallas.
   *
   * Se conserva por si Compras llega a subir facturas por su cuenta: ese dia
   * hace falta el endpoint del lado del back antes de llamar a esto.
   */
  async getAvailableLines(orderId: string): Promise<import('@/types/purchaseOrderRH.types').PurchaseOrderRHLine[]> {
    const response = await httpRequest<import('@/types/purchaseOrderRH.types').PurchaseOrderRHLine[]>(`${base}/${orderId}/available-lines`)
    return response!
  },

  /**
   * OJO: la ruta de admin no existe en el backend. Subir facturas y consultar
   * las lineas disponibles solo se puede desde el portal del proveedor, y de
   * eso se encarga features/proveedores/api/purchaseOrders.service.ts, que es
   * el que usan las pantallas.
   *
   * Se conserva por si Compras llega a subir facturas por su cuenta: ese dia
   * hace falta el endpoint del lado del back antes de llamar a esto.
   */
  async adjustInvoice(orderId: string, xmlFile: File, lineIds: string[], pdfFile?: File): Promise<void> {
    const formData = new FormData()
    formData.append('xml', xmlFile)
    if (pdfFile) formData.append('pdf', pdfFile)

    // Cambiamos join(',') por append iterativo para que NestJS lo lea como array
    lineIds.forEach(id => {
      formData.append('lineIds[]', id);
    });

    // El Content-Type no se pone a mano: con FormData lo arma el navegador con
    // su boundary, y ponerlo rompe la subida.
    await httpRequest(`${base}/${orderId}/invoices`, {
      method: 'POST',
      body: formData,
    })
  },

  async releaseLines(orderId: string, lineIds: string[]): Promise<import('@/types/purchaseOrderRH.types').ReleaseLinesResponse> {
    const response = await httpRequest<import('@/types/purchaseOrderRH.types').ReleaseLinesResponse>(
      `${base}/${orderId}/lines/release`,
      { method: 'POST', body: JSON.stringify({ lineIds }) },
    )
    return response!
  },

  async approveInvoice(orderId: string, invoiceId: string, creditDays: number): Promise<void> {
    await httpRequest(`${base}/${orderId}/invoices/${invoiceId}/approve`, {
      method: 'POST',
      body: JSON.stringify({ creditDays }),
    })
  },

  async rejectInvoice(orderId: string, invoiceId: string, reason: string): Promise<void> {
    await httpRequest(`${base}/${orderId}/invoices/${invoiceId}/reject`, {
      method: 'PATCH',
      body: JSON.stringify({ reason }),
    })
  },

  async observeInvoice(orderId: string, invoiceId: string, reason: string): Promise<void> {
    await httpRequest(`${base}/${orderId}/invoices/${invoiceId}/observe`, {
      method: 'PATCH',
      body: JSON.stringify({ reason }),
    })
  }
}
